import { onBeforeUnmount, reactive, watch, type MaybeRefOrGetter } from "vue";
import { toValue } from "vue";
import { prepareGepAiVoiceCallContext } from "@/api/HomeService";

const SCRIPT_ID = "gep-ai-voice-caller-script";
const MOUNT_SELECTOR = "#gep-ai-voice-caller-mount";
const STORAGE_KEY = "vouch-ava:gep:ai-voice-client-instance-id";

type CallerWidgetApi = { destroy: () => void };

declare global {
  interface Window {
    CallerWidget?: {
      initAsync?: (options: {
        mount: string;
        apiKey: string;
        widgetId: string;
        clientInstanceId: string;
        surface: "gep";
        guestLanguage?: string;
        locale?: string;
        prepareCallContext?: (activityId: string) => boolean | Promise<boolean>;
      }) => Promise<CallerWidgetApi | null>;
    };
  }
}

const apiKey = import.meta.env.VITE_VIDEO_CALLER_API_KEY as string | undefined;
const widgetUrl = import.meta.env.VITE_VIDEO_CALLER_URL as string | undefined;
const allowedOrigins =
  (import.meta.env.VITE_VIDEO_CALLER_ALLOWED_ORIGINS as string | undefined)
    ?.split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean)
    .flatMap((value) => {
      try {
        return [new URL(value).origin];
      } catch {
        return [];
      }
    }) ?? [];

let widget: CallerWidgetApi | null = null;
let scriptPromise: Promise<void> | null = null;
let initializationVersion = 0;

const createClientInstanceId = () =>
  `gep-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;

const getClientInstanceId = () => {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const next = createClientInstanceId();
    sessionStorage.setItem(STORAGE_KEY, next);
    return next;
  } catch {
    return createClientInstanceId();
  }
};

const isAllowedWidgetUrl = (value: string) => {
  try {
    const url = new URL(value);
    if (!import.meta.env.DEV && url.protocol !== "https:") return false;
    return (
      url.origin === window.location.origin ||
      allowedOrigins.includes(url.origin)
    );
  } catch {
    return false;
  }
};

const loadWidgetScript = async () => {
  if (window.CallerWidget?.initAsync) return;
  if (!widgetUrl || !isAllowedWidgetUrl(widgetUrl)) {
    throw new Error(
      "The AI voice caller URL is not configured or allowlisted.",
    );
  }

  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(
      SCRIPT_ID,
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Unable to load AI voice caller.")),
        {
          once: true,
        },
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = new URL("launcher.iife.js", widgetUrl).toString();
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Unable to load AI voice caller."));
    document.head.append(script);
  });

  await scriptPromise;
  if (!window.CallerWidget?.initAsync) {
    throw new Error("AI voice caller did not initialise.");
  }
};

export const destroyGepAiVoiceCaller = () => {
  initializationVersion += 1;
  widget?.destroy();
  widget = null;
};

export const useGepAiVoiceCaller = (input: {
  enabled: MaybeRefOrGetter<boolean>;
  merchantId: MaybeRefOrGetter<string | undefined>;
  language: MaybeRefOrGetter<string | undefined>;
  token: MaybeRefOrGetter<string | undefined>;
}) => {
  const state = reactive({ initialized: false, error: "" });

  const initialise = async () => {
    destroyGepAiVoiceCaller();
    const version = initializationVersion;
    state.initialized = false;
    state.error = "";

    const merchantId = toValue(input.merchantId)?.trim();
    if (!toValue(input.enabled) || !merchantId || !apiKey) return;

    try {
      await loadWidgetScript();
      if (
        version !== initializationVersion ||
        !toValue(input.enabled) ||
        merchantId !== toValue(input.merchantId)?.trim()
      )
        return;
      const clientInstanceId = getClientInstanceId();
      const initializedWidget =
        (await window.CallerWidget?.initAsync?.({
          mount: MOUNT_SELECTOR,
          apiKey,
          widgetId: merchantId,
          clientInstanceId,
          surface: "gep",
          guestLanguage: toValue(input.language) || "en",
          locale: toValue(input.language) || "en",
          prepareCallContext: (activityId) => {
            const token = toValue(input.token)?.trim();
            if (!token) return false;
            return prepareGepAiVoiceCallContext({
              token,
              activityId,
              clientInstanceId,
            });
          },
        })) ?? null;
      if (
        version !== initializationVersion ||
        !toValue(input.enabled) ||
        merchantId !== toValue(input.merchantId)?.trim()
      ) {
        initializedWidget?.destroy();
        return;
      }
      widget = initializedWidget;
      state.initialized = widget !== null;
    } catch (error) {
      state.error =
        error instanceof Error
          ? error.message
          : "Unable to start AI voice assistance.";
    }
  };

  const stop = watch(
    [
      () => toValue(input.enabled),
      () => toValue(input.merchantId),
    ],
    () => void initialise(),
    { immediate: true },
  );

  onBeforeUnmount(() => {
    stop();
    destroyGepAiVoiceCaller();
  });

  return { state, initialise };
};
